import { useState } from 'react';
import { Button } from './ui/button';
import { acquisitionSources } from '../lib/constants';
import { toast } from 'sonner';
import * as api from '../lib/api';

// One-time, mandatory modal shown to existing users (post-onboarding
// accounts created before this feature existed) who don't have an
// acquisition_source on file yet. Not dismissable — no backdrop-click or
// escape-key close, no close button — since the answer is required before
// continuing into the app. New users answer the same question inline
// during onboarding instead, so this never shows for them.
export default function AcquisitionSourceModal({ onSaved }) {
    const [source, setSource] = useState('');
    const [other, setOther] = useState('');
    const [busy, setBusy] = useState(false);

    const canSubmit = source && (source !== 'other' || other.trim().length > 0);

    const handleSubmit = async () => {
        if (!canSubmit || busy) return;
        setBusy(true);
        try {
            const updated = await api.updateProfile({
                acquisition_source: source,
                acquisition_source_other: source === 'other' ? other.trim() : '',
            });
            onSaved(updated);
        } catch (e) {
            toast.error('No se pudo guardar tu respuesta. Intenta de nuevo.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            // No backdrop-blur-sm here on purpose: this is a full-viewport
            // fixed overlay that can appear right after login, before the
            // user has even reached the rest of the app — same class of
            // GPU-compositing crash as the onboarding currency step and the
            // transaction category picker (see Onboarding.js, index.css
            // .glass-card, Sidebar.js mobile-nav). Bumped bg-black/70 to
            // /85 to keep the same visual weight without the blur.
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/85"
            data-testid="acquisition-source-modal"
        >
            <div className="w-full max-w-md glass-card rounded-2xl p-6 sm:p-8 noise-overlay relative animate-modal-in">
                <h2 className="text-xl sm:text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Poppins' }}>
                    Pregunta rápida 👀
                </h2>
                <p className="text-slate-400 text-sm mb-6">
                    Estamos mejorando MiPisto y queremos entender cómo la gente la descubre.
                    <br />¿Cómo encontraste MiPisto originalmente?
                </p>

                <div className="grid grid-cols-2 gap-2.5">
                    {acquisitionSources.map((opt) => {
                        const Icon = opt.icon;
                        const selected = source === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                data-testid={`acquisition-source-${opt.value}`}
                                onClick={() => setSource(opt.value)}
                                className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium text-left transition-all duration-200 ${selected
                                    ? 'border-violet-500/50 bg-violet-500/10 text-white'
                                    : 'border-slate-700/50 bg-slate-900/50 text-slate-400 hover:border-slate-600'
                                    }`}
                            >
                                <Icon className="w-4 h-4 shrink-0" />
                                {opt.label}
                            </button>
                        );
                    })}
                </div>

                {source === 'other' && (
                    <input
                        data-testid="acquisition-source-other-input"
                        type="text"
                        placeholder="Cuéntanos dónde..."
                        value={other}
                        onChange={(e) => setOther(e.target.value)}
                        className="w-full mt-3 bg-slate-950/50 border border-slate-700 focus:border-violet-500/50 h-11 rounded-xl px-3.5 text-white placeholder:text-slate-400 text-sm outline-none"
                    />
                )}

                <Button
                    data-testid="acquisition-source-submit"
                    onClick={handleSubmit}
                    disabled={!canSubmit || busy}
                    className="w-full mt-6 h-11 bg-violet-500 hover:bg-violet-600 text-on-brand rounded-xl font-semibold shadow-[0_0_20px_rgba(139,92,246,0.25)] transition-all duration-200 disabled:opacity-40 disabled:shadow-none"
                >
                    {busy ? 'Guardando...' : 'Continuar'}
                </Button>
            </div>
        </div>
    );
}
